
import FormMixin from "../../helpers/mixins/form";
import { Options } from "vue-class-component";
import Store from "../../store";
import Page from "../molecules/Page.vue";
import TableActionButton from "../atoms/TableActionButton.vue";
import SearchField from "../atoms/SearchField.vue";
import ActionButton from "../atoms/ActionButton.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import { InstructionLevel } from "../../types";
import {
  SystemApi,
  DepartmentApi,
  GetEmployeeProfile,
  GetApplicantProfile,
  ApplicantApi,
  UserTypeEnum,
  AttachTag,
  DetachTag
} from "../../autogen";
import { VueEternalLoading, LoadAction } from "@ts-pro/vue-eternal-loading";

type Tag = {
  tagId: string;
  name: string;
  inEdit: boolean;
  selected: boolean;
  color?: string;
};

@Options({
  components: {
    Page,
    SearchField,
    ActionButton,
    TableActionButton,
    VueEternalLoading,
    HelpIcon
  }
})
export default class Tags extends FormMixin {
  isOpenNewTagForm = false;
  memory = "";
  newTagName = "";
  formName = "タグ";
  userType: UserTypeEnum = UserTypeEnum.Employee;
  userName: string = "";
  department: string = "all";
  colors = ["#b9dff8", "#bef59c", "#ffd76c", "#f5fe01", "#ffd1cb", "#9ddaff", "#86e08c", "#ffb900", "#ffeb00", "#ffa99d"];
  tags: Tag[] = [];
  pageSize = 100;
  participants: { [key: string]: any }[] = [];
  header = [
    { sortable: true, key: "expand", width: "shrink", type: "selection" },
    { title: "名前", sortable: true, key: "name", width: "small" },
    { title: "部署", sortable: true, key: "departmentName", width: "small" },
    { title: "職種", sortable: true, key: "occupationName", width: "small" },
    {
      title: "付与済みタグ",
      key: "tags",
      width: 750,
      type: "tag",
      tagStyle: { display: "flex", flexWrap: "wrap" }
    },
    { title: "受検結果", type: "button", key: "result", width: 120 }
  ];
  inEdit: boolean = false;
  isReadableUserStats: boolean = false;
  sortKey: string = "";
  sortOrders: { [key: string]: number } = {};
  lastSortedKey: string = "";

  get nextColor(): string {
    return this.colors[this.tags.length % 10];
  }
  get inEditTag(): boolean {
    return this.tags.some(tag => tag.inEdit);
  }
  get disableAttachTags() {
    return (
      this.participants.filter(participant => participant.selected).length === 0 ||
      this.tags.filter(tag => tag.selected).length === 0
    );
  }
  async beforeMount() {
    this.checkReadableUserStats();
  }
  async mounted() {
    if (this.header) {
      this.header.forEach(head => {
        if (head.key) this.sortOrders[head.key] = 1;
        if (!head.type) head.type = "text";
      });
    }
    await this.fetchTags();
    this.fetchTableData();
  }
  get filteredParticipants() {
    let sortKey = this.sortKey;
    let order = this.sortOrders[sortKey] || 1;
    let participants = this.participants;
    participants = participants.map((d, index: number) => ({
      orgIndex: index,
      ...d
    }));
    if (sortKey && participants) {
      participants = participants.slice().sort((a, b) => {
        a = a[sortKey];
        b = b[sortKey];
        return (a === b ? 0 : a > b ? 1 : -1) * order;
      });
    }
    return participants;
  }
  checkReadableUserStats() {
    this.isReadableUserStats =
      this.userType === UserTypeEnum.Employee ? this.$role.isReadable("employeeStats") : this.$role.isReadable("applicantStats");
  }

  getTagColor(tag: Tag) {
    return this.colors[this.tags.findIndex(t => t.tagId === tag.tagId) % 10];
  }
  onSearch(): void {
    this.fetchTableData();
    this.checkReadableUserStats();
  }
  onClear(): void {
    this.userType = UserTypeEnum.Employee;
    this.userName = "";
    this.department = "all";
  }
  openNewTagForm() {
    this.isOpenNewTagForm = true;
  }
  closeNewTagForm() {
    this.isOpenNewTagForm = false;
    this.newTagName = "";
  }
  async addTag() {
    try {
      await new SystemApi().createTag({
        createTag: { name: this.newTagName, type: "person" }
      });
      this.fetchTags();
      this.isOpenNewTagForm = false;
      this.newTagName = "";
      Store.getInstance().setInstructionContent(InstructionLevel.Info, "タグを更新しました");
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
  editTag(tag: Tag): void {
    this.memory = tag.name;
    tag.inEdit = true;
  }
  cancelEdit(tag: Tag): void {
    tag.name = this.memory;
    this.memory = "";
    tag.inEdit = false;
  }
  async confirmEditTag(tag: Tag) {
    if (tag.name === this.memory) {
      this.memory = "";
      tag.inEdit = false;
      return;
    }
    try {
      await new SystemApi().updateTag({
        tagId: tag.tagId,
        updateTag: { name: tag.name, type: "person" }
      });
      tag.inEdit = false;
      this.fetchTableData();
      Store.getInstance().setInstructionContent(InstructionLevel.Info, "タグを更新しました");
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
  async deleteTag(tag: Tag) {
    this.onDelete(async () => {
      await new SystemApi().deleteTag({
        tagId: tag.tagId
      });
      this.fetchTags();
      this.fetchTableData();
    }, "タグを削除します。よろしいでしょうか？\n※従業員、応募者に付与したタグも全て削除されます。");
  }
  async attachTag() {
    const selectedTags = this.tags.filter(tag => tag.selected);
    const selectedUsers = this.participants.filter(participant => participant.selected);
    selectedUsers.map(user => user.id);
    let attachTag: AttachTag = { tagIds: selectedTags.map(tag => tag.tagId) };
    if (this.userType === UserTypeEnum.Employee) {
      attachTag.employeeIds = selectedUsers.map(user => user.id);
    } else if (this.userType === UserTypeEnum.Applicant) {
      attachTag.applicantIds = selectedUsers.map(user => user.id);
    }

    try {
      const response = await new SystemApi().attachTag({
        attachTag
      });
      if (response.data && response.data.message) {
        if (response.data.name_list && response.data.name_list.length > 0) {
          Store.getInstance().setInstructionContent(
            InstructionLevel.Alert,
            response.data.message + "\n" + response.data.name_list.join(", ")
          );
        } else {
          Store.getInstance().setInstructionContent(InstructionLevel.Info, response.data.message);
        }
      }
      this.fetchTableData();
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
  async detachTag(tagId: string, userId: string) {
    let detachTag: DetachTag = {};
    if (this.userType === UserTypeEnum.Employee) {
      detachTag.employeeIds = [userId];
    } else if (this.userType === UserTypeEnum.Applicant) {
      detachTag.applicantIds = [userId];
    }
    try {
      this.onDelete(async () => {
        await new SystemApi().detachTag({
          tagId,
          detachTag
        });
        this.fetchTableData();
      }, "タグを除去します。よろしいでしょうか？");
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }

  sortBy(key: string, type: string) {
    if (type !== "text") return;
    this.sortKey = key;
    this.sortOrders[key] = this.sortOrders[key] * -1;
    this.lastSortedKey = key;
  }

  onSelectionChange(event: Event, index: number) {
    const target = event.target as HTMLInputElement;
    this.participants[index].selected = target.checked;
  }
  onSelectionAllChange(event: Event) {
    const target = event.target as HTMLInputElement;
    if (this.participants) {
      this.participants.forEach(participant => {
        participant.selected = target.checked;
      });
    }
  }
  async fetchTags() {
    try {
      const tags = await new SystemApi().getTags({
        type: "person"
      });
      this.tags = tags.data.map((tag, index) => ({
        tagId: tag.tagId!,
        name: tag.name!,
        inEdit: false,
        selected: false,
        color: this.colors[index % 10]
      }));
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
  async fetchTableData() {
    let response;
    try {
      if (this.userType === UserTypeEnum.Employee) {
        response = await new DepartmentApi().getEmployees(
          Object.assign({
            limit: "100",
            name: this.userName ? this.userName : undefined,
            departmentId: this.department
          })
        );

        this.participants = (response.data.lists as GetEmployeeProfile[]).map(datum =>
          Object.assign(datum, {
            name: `${datum.lastName} ${datum.firstName}`,
            department: datum.department,
            departmentName: datum.department?.name || "-",
            occupationName: datum.occupation?.name || "-",
            tags: datum.tags
              ? datum.tags.map((tag, index) => ({
                  id: tag.id,
                  name: tag.name,
                  color: this.tags.find(t => t.tagId === tag.id)!.color
                }))
              : [],
            selected: false,
            tested: datum.lastExamDate ? true : false
          })
        );
      } else if (this.userType === UserTypeEnum.Applicant) {
        response = await new ApplicantApi().getApplicants(
          Object.assign({
            limit: "100",
            name: this.userName ? this.userName : undefined,
            departments: this.department?.length ? this.department : undefined
          })
        );

        this.participants = (response.data.lists as GetApplicantProfile[]).map(datum =>
          Object.assign(datum, {
            name: `${datum.lastName} ${datum.firstName}`,
            department: datum.department,
            departmentName: datum.department?.name || "-",
            occupationName: datum.occupation?.name || "-",
            tags: datum.tags
              ? datum.tags.map((tag, index) => ({ id: tag.id, name: tag.name, color: this.colors[index % 10] }))
              : [],
            selected: false,
            tested: datum.lastExamDate ? true : false
          })
        );
      }
    } catch (err) {
      let errMessage = "エラーが発生しました";
      if (err.response && err.response.data) {
        errMessage = err.response.data.detail;
      }
      Store.getInstance().setInstructionContent(InstructionLevel.Alert, errMessage);
    }
  }
  async load(action: LoadAction): Promise<any> {
    if (!this.participants) return;
    const loadedData = this.participants.slice(this.participants.length, this.participants.length + this.pageSize);
    if (loadedData.length) {
      const _sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      await _sleep(350);
      this.participants.push(...loadedData);
      action.loaded(this.participants.length, this.pageSize);
    } else {
      action.noMore();
    }
  }
  async onColumnClick(index: number, key: string, event: Event) {
    const rowData = this.participants[index];
    const location = { row: index, col: key, el: event && event.target, rowData };
    if (location.col === "result" && location.rowData) {
      this.$router.push({
        name: this.userType === UserTypeEnum.Employee ? "EmployeeStats" : "ApplicantStats",
        params: {
          clientId: this.$route.params["clientId"],
          peopleId: location.rowData.id
        }
      });
    }
  }
}
